import { Field } from "@app/types/Cue";
import { Name, Type } from "@app/types/enums";
import { useTextRangesWithAnnotations } from "@hooks/useTextRangesWithAnnotations";
import { cn, getFieldValue } from "@src/app/util/helpers";

export type SubHeadingProps = {
  className?: string;
  fields: Field[];
  forceHeadingCss?: boolean;
};

export const headingClassName = (headingTag: string): string => {
  switch (headingTag) {
    case "h2":
      return "text-[21px] md:text-4xl -tracking-5% font-lucida font-bold";
    case "h3":
      return "text-lg md:text-xl -tracking-5% font-lucida font-bold";
    case "h4":
      return "text-base md:text-lg -tracking-5% font-lucida font-bold";
    case "h5":
      return "text-3xl -tracking-5% font-lucida font-normal";
    case "h6":
      return "text-xl -tracking-5% font-lucida font-normal";
    default:
      return "";
  }
};

export default function SubHeading({
  className,
  fields,
  forceHeadingCss,
}: SubHeadingProps): React.ReactElement {
  const text =
    fields.find((field) => field.name === Type.SubHeadField)?.value || "";
  const heading = fields.find((field) => field.name === Type.Headings)?.value;
  const cssClass = getFieldValue(fields, Name.CssClass);
  const cssClassString = typeof cssClass === "string" ? cssClass : undefined;
  const annotations =
    fields.find((field) => field.name === Type.SubHeadField)?.annotations || [];
  const HeadingTag = heading as "span" | "h2" | "h3" | "h4" | "h5" | "h6";
  const { componentToRender } = useTextRangesWithAnnotations(text, annotations);

  const headingClass =
    (forceHeadingCss && headingClassName(heading || "")) || "";

  return (
    <HeadingTag
      className={cn(className, headingClass, cssClassString)}
      data-story-element={Type.SubHead}
      data-testid="sub-heading-component"
    >
      {componentToRender}
    </HeadingTag>
  );
}
